/**
 * Auth Page - Unified Login/Register Page
 * Handles both login and registration with all 3 auth methods
 * Based on config: Server Auth, Firebase OAuth, Google OAuth
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import { useAuth } from '../context/AuthContext';
import { notificationService } from '../services/notificationService';
import config from '../config';
import Button from '../components/ui/Button';
import { logger } from '../utils/logger';
import SEO from '../components/seo/SEO';

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { login, refreshAuthFromStorage } = useAuth();
  const initialMode = searchParams.get('mode') === 'register' ? 'register' : 'login';
  const isRegister = initialMode === 'register';
  
  const [mode, setMode] = useState<'login' | 'register'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingOAuth, setIsProcessingOAuth] = useState(false);

  const encodeOAuthState = useCallback((payload: { returnUrl: string }) => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    try {
      return window.btoa(encodeURIComponent(JSON.stringify(payload)));
    } catch (error) {
      logger.child('AUTH').error('Failed to encode OAuth state:', error);
      return undefined;
    }
  }, []);

  const decodeOAuthState = useCallback((value?: string): { returnUrl?: string } => {
    if (!value || typeof window === 'undefined') {
      return {};
    }

    try {
      return JSON.parse(decodeURIComponent(window.atob(value)));
    } catch (error) {
      logger.child('AUTH').error('Failed to decode OAuth state:', error);
      return {};
    }
  }, []);

  const clearOAuthParams = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      ['code', 'scope', 'authuser', 'prompt', 'state', 'error', 'error_description'].forEach((param) => {
        next.delete(param);
      });
      return next;
    });
  }, [setSearchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Set a timeout to prevent stuck loading state
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
      notificationService.error('Request timed out. Please try again.');
    }, 30000); // 30 second timeout
    
    try {
      if (mode === 'login') {
        // Login with credentials through AuthContext to sync state
        await login('credentials', { email, password });
        clearTimeout(timeoutId);
        notificationService.success('Welcome back!');
      } else {
        // Register new user
        await authService.register({name, email, password, password_confirmation: password});
        clearTimeout(timeoutId);
        refreshAuthFromStorage();
        notificationService.success('Account created successfully!');
      }
      
      // Redirect to home or return URL
      const returnUrl = searchParams.get('returnUrl') || '/';
      navigate(returnUrl);
    } catch (error: any) {
      clearTimeout(timeoutId);
      logger.child('AUTH').error('Auth error:', error);
      notificationService.error(error.message || 'Authentication failed');
    } finally {
      clearTimeout(timeoutId);
      setIsLoading(false);
    }
  };

  const handleGoogleOAuth = () => {
    if (!config.AUTH.ENABLE_GOOGLE_OAUTH) {
      notificationService.error('Google OAuth is not enabled');
      return;
    }

    if (!config.GOOGLE_OAUTH.CLIENT_ID || !config.GOOGLE_OAUTH.REDIRECT_URI) {
      notificationService.error('Google OAuth is not configured correctly. Please set client ID and redirect URI.');
      return;
    }

    const returnUrl = searchParams.get('returnUrl') || '/';

    if (typeof window !== 'undefined') {
      try {
        sessionStorage.setItem('oauth_return_url', returnUrl);
      } catch (error) {
        logger.child('AUTH').warn('Failed to persist OAuth return URL:', error);
      }
    }

    try {
      const stateValue = encodeOAuthState({ returnUrl });
      authService.redirectToGoogleOAuth(stateValue);
    } catch (error: any) {
      logger.child('AUTH').error('Failed to start Google OAuth flow:', error);
      notificationService.error(error.message || 'Unable to start Google OAuth flow');
    }
  };

  useEffect(() => {
    if (!config.AUTH.ENABLE_GOOGLE_OAUTH) {
      return;
    }

    const oauthError = searchParams.get('error');
    if (oauthError) {
      const description = searchParams.get('error_description');
      notificationService.error(description || `Google OAuth error: ${oauthError}`);
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('oauth_return_url');
      }
      clearOAuthParams();
      return;
    }

    if (isProcessingOAuth) {
      return;
    }

    const code = searchParams.get('code');
    if (!code) {
      return;
    }

    const callbackResult = authService.parseGoogleOAuthCallback(window.location.href);
    if (!callbackResult?.code) {
      clearOAuthParams();
      return;
    }

    const stateData = decodeOAuthState(callbackResult.state);
    let returnUrl = stateData.returnUrl || searchParams.get('returnUrl') || '/';

    if (typeof window !== 'undefined') {
      const storedReturnUrl = sessionStorage.getItem('oauth_return_url');
      if (storedReturnUrl) {
        returnUrl = storedReturnUrl;
        sessionStorage.removeItem('oauth_return_url');
      }
    }

    setIsProcessingOAuth(true);
    setIsLoading(true);

    (async () => {
      try {
        await authService.loginWithGoogleOAuth(callbackResult.code);
        refreshAuthFromStorage();
        notificationService.success('Welcome!');
        navigate(returnUrl, { replace: true });
      } catch (error: any) {
        logger.child('AUTH').error('Google OAuth login failed:', error);
        notificationService.error(error.message || 'Google OAuth authentication failed');
      } finally {
        clearOAuthParams();
        setIsLoading(false);
        setIsProcessingOAuth(false);
      }
    })();
  }, [clearOAuthParams, decodeOAuthState, encodeOAuthState, isProcessingOAuth, navigate, searchParams]);

  const handleFirebaseAuth = async () => {
    if (!config.AUTH.ENABLE_FIREBASE_AUTH) {
      notificationService.error('Firebase Auth is not enabled');
      return;
    }

    setIsLoading(true);
    
    // Set a timeout to prevent stuck loading state
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
      notificationService.error('Request timed out. Please try again.');
    }, 30000); // 30 second timeout
    
    try {
      await login('google');
      clearTimeout(timeoutId);
      notificationService.success('Welcome!');
      const returnUrl = searchParams.get('returnUrl') || '/';
      navigate(returnUrl);
    } catch (error: any) {
      clearTimeout(timeoutId);
      logger.child('AUTH').error('Firebase auth error:', error);
      notificationService.error(error.message || 'Firebase authentication failed');
    } finally {
      clearTimeout(timeoutId);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <SEO
        title={isRegister ? 'Create Account - PromptShare' : 'Sign In - PromptShare'}
        description={isRegister ? 'Join PromptShare to discover, save, and share the best AI prompts.' : 'Sign in to your PromptShare account.'}
        url={`https://promptshare.pro/${isRegister ? 'register' : 'login'}`}
        noindex={true}
      />
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {mode === 'login' ? 'Sign in to your account' : 'Create your account'}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {mode === 'login' ? (
            <>
              Or{' '}
              <button
                onClick={() => setMode('register')}
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                create a new account
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button
                onClick={() => setMode('login')}
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Sign in
              </button>
            </>
          )}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Server Auth Form */}
          {config.AUTH.ENABLE_SERVER_AUTH && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {mode === 'register' && (
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <div className="mt-1">
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      required={mode === 'register'}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? 'Please wait...' : (mode === 'login' ? 'Sign in' : 'Sign up')}
                </Button>
              </div>
            </form>
          )}

          {/* OAuth Buttons */}
          {(config.AUTH.ENABLE_FIREBASE_AUTH || config.AUTH.ENABLE_GOOGLE_OAUTH) && (
            <>
              {config.AUTH.ENABLE_SERVER_AUTH && (
                <div className="mt-6">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">Or continue with</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 grid grid-cols-1 gap-3">
                {/* Firebase Auth (Google) */}
                {config.AUTH.ENABLE_FIREBASE_AUTH && (
                  <Button
                    type="button"
                    onClick={handleFirebaseAuth}
                    variant="outline"
                    className="w-full flex items-center justify-center"
                    disabled={isLoading}
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    <span>Google</span>
                  </Button>
                )}

                {/* Direct Google OAuth */}
                {config.AUTH.ENABLE_GOOGLE_OAUTH && (
                  <Button
                    type="button"
                    onClick={handleGoogleOAuth}
                    variant="outline"
                    className="w-full flex items-center justify-center"
                    disabled={isLoading}
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    <span>Google</span>
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
